/**
 * Hooks Dashboard Unifiés
 * Phase 3.2 - Optimisation avec réutilisation maximale
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import dashboardUnifiedService from '../services/dashboardUnifiedService';
import { DASHBOARD_TYPES, STATS_PERIODS } from '../constants/dashboardConstants';

/**
 * Hook principal pour le dashboard unifié
 */
export const useDashboardUnified = (type, options = {}) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const {
    period = STATS_PERIODS.MONTH,
    customStart,
    customEnd,
    widgets,
    autoRefresh = false,
    refreshInterval = 300000 // 5 minutes
  } = options;

  // Mémoriser les options pour éviter les re-renders inutiles
  const memoizedOptions = useMemo(() => ({
    period,
    customStart,
    customEnd,
    widgets
  }), [period, customStart, customEnd, widgets]);

  // Fonction de récupération des données
  const fetchDashboard = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);

      let response;
      if (type === DASHBOARD_TYPES.OWNER) {
        response = await dashboardUnifiedService.getOwnerDashboard(memoizedOptions);
        console.log('🔥 HOOK - RESPONSE OWNER REÇUE:', response);
      } else if (type === DASHBOARD_TYPES.RENTER) {
        response = await dashboardUnifiedService.getRenterDashboard(memoizedOptions);
        console.log('🔥 HOOK - RESPONSE RENTER REÇUE:', response);
      } else {
        throw new Error(`Type de dashboard invalide: ${type}`);
      }

      // Extraire data si structure {success, data, message}
      const data = response?.data || response;
      console.log('🔥 HOOK - DATA FINALE EXTRAITE:', data);
      setDashboard(data);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Erreur récupération dashboard:', err);
      setError(err.message || 'Erreur lors du chargement du dashboard');
    } finally {
      setLoading(false);
    }
  }, [type, memoizedOptions]);

  // Fonction de rafraîchissement
  const refresh = useCallback(async () => {
    await fetchDashboard(false);
  }, [fetchDashboard]);

  // Chargement initial
  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      refresh();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, refresh]);

  return {
    dashboard,
    loading,
    error,
    lastUpdated,
    refresh,
    fetchDashboard
  };
};

/**
 * Hook spécialisé pour le dashboard propriétaire
 */
export const useOwnerDashboard = (options = {}) => {
  return useDashboardUnified(DASHBOARD_TYPES.OWNER, options);
};

/**
 * Hook spécialisé pour le dashboard locataire
 */
export const useRenterDashboard = (options = {}) => {
  return useDashboardUnified(DASHBOARD_TYPES.RENTER, options);
};

/**
 * Hook pour un widget spécifique
 */
export const useWidget = (type, widgetName, options = {}) => {
  const [widget, setWidget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    period = STATS_PERIODS.MONTH,
    customStart,
    customEnd,
    autoRefresh = false,
    refreshInterval = 300000
  } = options;

  const memoizedOptions = useMemo(() => ({
    period,
    customStart,
    customEnd
  }), [period, customStart, customEnd]);

  const fetchWidget = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await dashboardUnifiedService.getWidget(type, widgetName, memoizedOptions);
      setWidget(data);
    } catch (err) {
      console.error(`Erreur widget ${widgetName}:`, err);
      setError(err.message || `Erreur lors du chargement du widget ${widgetName}`);
    } finally {
      setLoading(false);
    }
  }, [type, widgetName, memoizedOptions]);

  const refresh = useCallback(async () => {
    await fetchWidget();
  }, [fetchWidget]);

  useEffect(() => {
    fetchWidget();
  }, [fetchWidget]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(refresh, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, refresh]);

  return {
    widget,
    loading,
    error,
    refresh
  };
};

/**
 * Hook pour les métriques rapides
 */
export const useMetrics = (type, options = {}) => {
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    period = STATS_PERIODS.MONTH,
    autoRefresh = true,
    refreshInterval = 180000 // 3 minutes pour les métriques
  } = options;

  const memoizedOptions = useMemo(() => ({
    period
  }), [period]);

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await dashboardUnifiedService.getMetrics(type, memoizedOptions);
      setMetrics(data);
    } catch (err) {
      console.error('Erreur métriques:', err);
      setError(err.message || 'Erreur lors du chargement des métriques');
    } finally {
      setLoading(false);
    }
  }, [type, memoizedOptions]);

  const refresh = useCallback(async () => {
    await fetchMetrics();
  }, [fetchMetrics]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Auto-refresh pour les métriques
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(refresh, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, refresh]);

  return {
    metrics,
    loading,
    error,
    refresh
  };
};

/**
 * Hook pour les préférences dashboard (compatibilité)
 */
export const useDashboardPreferences = () => {
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPreferences = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await dashboardUnifiedService.getUserPreferences();
      setPreferences(data);
    } catch (err) {
      console.error('Erreur préférences:', err);
      setError(err.message || 'Erreur lors du chargement des préférences');
    } finally {
      setLoading(false);
    }
  }, []);

  const savePreferences = useCallback(async (newPreferences) => {
    try {
      setError(null);
      const data = await dashboardUnifiedService.saveUserPreferences(newPreferences);
      setPreferences(data);
      return data;
    } catch (err) {
      console.error('Erreur sauvegarde préférences:', err);
      setError(err.message || 'Erreur lors de la sauvegarde des préférences');
      throw err;
    }
  }, []);

  const resetToDefault = useCallback(async () => {
    try {
      setError(null);
      const data = await dashboardUnifiedService.resetToDefault();
      setPreferences(data);
      return data;
    } catch (err) {
      console.error('Erreur réinitialisation:', err);
      setError(err.message || 'Erreur lors de la réinitialisation');
      throw err;
    }
  }, []);

  const updateWidgetsLayout = useCallback(async (layout) => {
    try {
      setError(null);
      const data = await dashboardUnifiedService.updateWidgetsLayout(layout);
      setPreferences(data);
      return data;
    } catch (err) {
      console.error('Erreur mise à jour layout:', err);
      setError(err.message || 'Erreur lors de la mise à jour du layout');
      throw err;
    }
  }, []);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  return {
    preferences,
    loading,
    error,
    savePreferences,
    resetToDefault,
    updateWidgetsLayout,
    refresh: fetchPreferences
  };
};

/**
 * Hook pour le dashboard automatique
 */
export const useAutoDashboard = (options = {}) => {
  const [dashboard, setDashboard] = useState(null);
  const [detectedType, setDetectedType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    period = STATS_PERIODS.MONTH,
    customStart,
    customEnd,
    autoRefresh = false,
    refreshInterval = 300000
  } = options;

  const memoizedOptions = useMemo(() => ({
    period,
    customStart,
    customEnd
  }), [period, customStart, customEnd]);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await dashboardUnifiedService.getAutoDashboard(memoizedOptions);
      setDashboard(data);
      setDetectedType(data.auto_detected_type);
    } catch (err) {
      console.error('Erreur dashboard automatique:', err);
      setError(err.message || 'Erreur lors du chargement du dashboard');
    } finally {
      setLoading(false);
    }
  }, [memoizedOptions]);

  const refresh = useCallback(async () => {
    await fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(refresh, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, refresh]);

  return {
    dashboard,
    detectedType,
    loading,
    error,
    refresh
  };
};

/**
 * Hook pour la gestion des périodes
 */
export const usePeriodSelector = (initialPeriod = STATS_PERIODS.MONTH) => {
  const [period, setPeriod] = useState(initialPeriod);
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);

  const updatePeriod = useCallback((newPeriod, start = null, end = null) => {
    setPeriod(newPeriod);
    if (newPeriod === STATS_PERIODS.CUSTOM) {
      setCustomStart(start);
      setCustomEnd(end);
    } else {
      setCustomStart(null);
      setCustomEnd(null);
    }
  }, []);

  const resetToDefault = useCallback(() => {
    setPeriod(STATS_PERIODS.MONTH);
    setCustomStart(null);
    setCustomEnd(null);
  }, []);

  return {
    period,
    customStart,
    customEnd,
    updatePeriod,
    resetToDefault,
    isCustom: period === STATS_PERIODS.CUSTOM
  };
};
